import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Globe, 
  FileText, 
  Code, 
  Copy, 
  Check,
  ExternalLink,
  Lock,
  Zap,
  Database,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const APIDocsPage = () => {
  const [copied, setCopied] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(null), 2000);
  };

  const endpoints = [
    {
      category: "Translation",
      items: [
        { method: "POST", path: "/api/translate", desc: "Translate text between EN-CA and FR-CA" },
        { method: "POST", path: "/api/translate/stream", desc: "Stream translation with SSE" },
        { method: "POST", path: "/api/translate/batch", desc: "Batch translate multiple texts" },
      ]
    },
    {
      category: "Authentication",
      items: [
        { method: "POST", path: "/api/auth/register", desc: "Create new user account" },
        { method: "POST", path: "/api/auth/login", desc: "Login and get JWT token" },
        { method: "POST", path: "/api/auth/keys", desc: "Create API key for M2M" },
      ]
    },
    {
      category: "Terminology",
      items: [
        { method: "GET", path: "/api/terminology", desc: "List locked terms" },
        { method: "POST", path: "/api/terminology", desc: "Add new term (Admin)" },
        { method: "POST", path: "/api/terminology/bulk", desc: "Bulk import terms" },
      ]
    },
    {
      category: "Monitoring",
      items: [
        { method: "GET", path: "/api/health", desc: "Health check" },
        { method: "GET", path: "/api/sla/stats", desc: "SLA metrics (Admin)" },
        { method: "GET", path: "/api/metrics", desc: "Performance metrics" },
      ]
    },
    {
      category: "Backup",
      items: [
        { method: "GET", path: "/api/backup/terminology", desc: "Export terminology" },
        { method: "GET", path: "/api/backup/history", desc: "Export translation history" },
        { method: "POST", path: "/api/backup/restore/terminology", desc: "Restore from backup" },
      ]
    },
  ];

  const codeExamples = {
    curl: `curl -X POST "${BACKEND_URL}/api/translate" \\
  -H "X-API-Key: mt_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "Heavy snowfall warning in effect",
    "source_lang": "en-ca",
    "target_lang": "fr-ca"
  }'`,
    python: `import requests

response = requests.post(
    "${BACKEND_URL}/api/translate",
    headers={"X-API-Key": "mt_your_api_key"},
    json={
        "text": "Heavy snowfall warning in effect",
        "source_lang": "en-ca",
        "target_lang": "fr-ca"
    }
)

print(response.json()["translated_text"])`,
    javascript: `const response = await fetch("${BACKEND_URL}/api/translate", {
  method: "POST",
  headers: {
    "X-API-Key": "mt_your_api_key",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    text: "Heavy snowfall warning in effect",
    source_lang: "en-ca",
    target_lang: "fr-ca"
  })
});

const result = await response.json();
console.log(result.translated_text);`
  };

  const [activeTab, setActiveTab] = useState("curl");

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Globe className="w-6 h-6 text-blue-600" />
            <span className="font-bold text-xl tracking-tight text-gray-900">MeteoTranslate</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/translate" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Translate
            </Link>
            <Link to="/terminology" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Terminology
            </Link>
            <Link to="/metrics" className="text-sm font-medium text-gray-600 hover:text-gray-900">
              Metrics
            </Link>
            <Link to="/docs" className="text-sm font-medium text-blue-600">
              API Docs
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">API Documentation</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl">
            Integrate MeteoTranslate into your applications with our RESTful API. 
            Translate meteorological content between Canadian English and Canadian French 
            with enterprise-grade reliability.
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <Lock className="w-6 h-6 text-blue-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Authentication</h3>
            <p className="text-sm text-gray-600">JWT tokens or API keys for M2M</p>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <Zap className="w-6 h-6 text-amber-500 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">Rate Limits</h3>
            <p className="text-sm text-gray-600">100 req/min standard, 10 req/min batch</p>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <Database className="w-6 h-6 text-emerald-500 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">RAG Enhanced</h3>
            <p className="text-sm text-gray-600">74,000+ training vectors</p>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <Shield className="w-6 h-6 text-purple-500 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-1">99.9% SLA</h3>
            <p className="text-sm text-gray-600">Enterprise reliability</p>
          </div>
        </div>

        {/* Code Examples */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-12">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-900">Quick Start</h2>
          </div>
          <div className="border-b border-gray-200">
            <div className="flex gap-1 px-6 pt-4">
              {["curl", "python", "javascript"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === tab
                      ? "bg-gray-900 text-white"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="relative">
            <pre className="p-6 bg-gray-900 text-gray-100 text-sm overflow-x-auto rounded-b-lg">
              <code>{codeExamples[activeTab]}</code>
            </pre>
            <button
              onClick={() => copyToClipboard(codeExamples[activeTab], activeTab)}
              className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded text-gray-300"
            >
              {copied === activeTab ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Endpoints */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-900">API Endpoints</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {endpoints.map((section) => (
              <div key={section.category} className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{section.category}</h3>
                <div className="space-y-3">
                  {section.items.map((endpoint, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <span
                        className={`px-2 py-1 text-xs font-mono font-bold rounded ${
                          endpoint.method === "GET"
                            ? "bg-green-100 text-green-700"
                            : endpoint.method === "POST"
                            ? "bg-blue-100 text-blue-700"
                            : endpoint.method === "DELETE"
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {endpoint.method}
                      </span>
                      <code className="text-sm font-mono text-gray-800 flex-1">{endpoint.path}</code>
                      <span className="text-sm text-gray-600">{endpoint.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OpenAPI Link */}
        <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">OpenAPI Specification</h3>
              <p className="text-sm text-blue-700">
                Download the full OpenAPI 3.0 specification for code generation and testing
              </p>
            </div>
            <a
              href={`${BACKEND_URL}/api/openapi.json`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Code className="w-4 h-4" />
              OpenAPI JSON
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* GitHub Docs Link */}
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            For detailed documentation, see the{" "}
            <a
              href="https://github.com/your-repo/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              GitHub Documentation
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default APIDocsPage;
